import { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { AUDITLOG_SQLA_MAX_DAYS, GLOBALSETTINGS, SYSTEMSETTINGS } from '../data/constants';
import { getAuditLogs } from '../services/auditLogService';
import { AuditLogDto, AuditLogRequest } from '../types/AuditLog';
import { differenceInCalendarDays, format } from 'date-fns';
import { useSettingValue } from '../services/useSettingValue';
import { DataGridProProps, useGridApiRef } from '@mui/x-data-grid-pro';
import React from 'react';
import { filterDataBySearch, handleScrollPositionChange } from '../utils/commonHelper';

/**
 * Epic 80054 - true when an Audit Log query must NOT be issued because it would scan an
 * unbounded slice of the log: a range wider than AUDITLOG_SQLA_MAX_DAYS with no user selected
 * to scope it. Shared by the Go handler (which shows the guard message) and loadData (which
 * also runs on search/pagination changes), so the two cannot disagree.
 *
 * Applies to BOTH sources. It was originally SQLA-only (Epic 80054 AC5 said Portal behaviour
 * must be unchanged), and no longer is: an unscoped wide Portal range hits the same problem,
 * so the restriction is now a property of the range, not of the source. Renamed from
 * isSqlaRangeBlocked accordingly - a name saying SQLA would now be wrong. Note the API-side
 * mirror, AuditLogGuard.IsSqlaRangeBlocked, is still SQLA-only, so the SPA is deliberately
 * the stricter of the two; that is the safe direction (the server never rejects a range the
 * grid considers valid), but the two are no longer literal mirrors.
 */
export const isAuditRangeBlocked = (
  fromDate: Date | null,
  toDate: Date | null,
  selectedUsers: number[]
): boolean => {
  if (!fromDate || !toDate) return false;
  if ((selectedUsers?.length ?? 0) > 0) return false;
  return differenceInCalendarDays(toDate, fromDate) > AUDITLOG_SQLA_MAX_DAYS;
};

const useAuditLog = (fromDate: Date | null, toDate: Date | null, selectedUsers: number[], source: string) => {
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.RESOURCE_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: AuditLogDto[] | null; total: number }>({
    data: null,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });
  const [handleGoTrigger, setHandleGoTrigger] = useState(-1);
  const [searchResult, setSearchResult] = useState('');
  const isClientSearchEvaluated = useRef(false);
  const allData = useRef<AuditLogDto[]>([]);
  const isFetching = useRef(false);
  //* Bug 78833 - see runLoadData: one audit-log fetch in flight, one re-run queued behind it.
  const isLoadInFlight = useRef(false);
  const isReloadQueued = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = useRef(true);
  const apiRef = useGridApiRef();

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent('scrollPositionChange', handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);
  const isSourceMounted = useRef(false);

  const handleOnRowsScrollEnd: DataGridProProps['onRowsScrollEnd'] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return; // Prevent further API calls when all rows are loaded
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    //* Bug 78833 - return the SAME object when the page is already 0. Rebuilding it
    //* unconditionally changed the reference on every call, so the effect below re-ran and
    //* issued a duplicate query even though neither the search text nor the page had moved.
    //* Switching Source clears the search box, which calls this twice - once from the clear
    //* signal, once from the debounce that follows ~1s later - and that fired two identical
    //* unfiltered queries concurrently over the retained date range.
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, []);


  const loadData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    //* Epic 80054 - handleGoClick blocks this combination before it ever triggers a fetch,
    //* but loadData also runs on search-text and pagination changes, which would still fire
    //* the unbounded query. Guarding here too means no code path can issue it. Returning
    //* without touching pageState leaves the grid as-is and never sets `loading`, so no
    //* overlay appears.
    if (isAuditRangeBlocked(fromDate, toDate, selectedUsers)) return;
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated.current && isFirstLoaded.current) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearch(allData.current, lowerText);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData.current ?? [],
          total: allData.current?.length,
        });
      }
    }
    else {
      const auditLogRequest: AuditLogRequest = {
        fromDate: format(fromDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        toDate: format(toDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        userIds: selectedUsers ?? [],
        source
      }
      const auditLogData = await executeApi<AuditLogRequest, AuditLogDto[]>(getAuditLogs, auditLogRequest);
      if (auditLogData && auditLogData.length > 0) {
        if (!isFirstLoaded.current && auditLogData[0]?.totalRecords <= paginationModel.pageSize) {
          isClientSearchEvaluated.current = true;
          allData.current = auditLogData;
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.auditLogId)); // Track existing IDs
          const newData = auditLogData.filter((row) => !existingIds.has(row.auditLogId)); // Filter duplicates

          return {
            data: isNewSearch ? auditLogData : [...(prev.data || []), ...newData],
            total: auditLogData.length > 0 && isNewSearch ? auditLogData[0].totalRecords : prev.total,
          };
        });
      }
      else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef.current.getScrollPosition().top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedUsers, paginationModel, searchResult, source]);

  //* Bug 78833 - keep a ref on the newest loadData closure so runLoadData can re-run the
  //* CURRENT query after the in-flight one finishes. Declared above the effects that call
  //* runLoadData so it is refreshed first within the same commit.
  const loadDataRef = useRef(loadData);
  useEffect(() => {
    loadDataRef.current = loadData;
  });

  /**
   * Bug 78833 - serialises audit-log fetches: one in flight at a time, with at most one
   * re-run queued behind it.
   *
   * Every Source switch fires a fresh query over whatever date range is still in the filter,
   * with the search cleared. Switching again before that returns used to start another one
   * alongside it, so a few quick switches stacked several unfiltered page-size-1000 queries
   * on the same connection. They contended and blew past the 30s SQL command timeout, which
   * surfaced as an intermittent HTTP 500 (SqlException: Execution Timeout Expired).
   *
   * Aborting the HTTP request would not help: the API does not pass its CancellationToken
   * down to Dapper, so the query keeps running server-side after the client gives up. The
   * only thing that relieves the database is not issuing the overlapping query at all.
   */
  const runLoadData = useCallback(async () => {
    if (isLoadInFlight.current) {
      isReloadQueued.current = true;
      return;
    }
    isLoadInFlight.current = true;
    try {
      do {
        isReloadQueued.current = false;
        await loadDataRef.current();
      } while (isReloadQueued.current);
    } finally {
      isLoadInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);


  useEffect(() => {
    if (!isSourceMounted.current) {
      isSourceMounted.current = true;
      return;
    }
    isClientSearchEvaluated.current = false;
    isFirstLoaded.current = false;
    allData.current = [];
  }, [source]);

  useEffect(() => {
    if (isPaginationInitialized && handleGoTrigger > -1) {
      isClientSearchEvaluated.current = false;
      allData.current = [];
      isFirstLoaded.current = false;
      if (prevPagination.current.page === 0) {
        runLoadData();
      }
      else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoTrigger, isPaginationInitialized]);

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (isPaginationInitialized || hasChanged || searchChanged) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      runLoadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized, paginationModel, searchResult])

  return { loading, pageState, paginationModel, setHandleGoTrigger, handleOnRowsScrollEnd, apiRef, handleSearchText };
};

export default useAuditLog;
